import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// The vault: your own file storage, kept in a Railway storage bucket. Drop in
// anything you want to keep — PDFs, books, images, voice memos, documents —
// sort it into folders, and open it from any device. Files you upload
// elsewhere land here on their own: fics' AO3 files, novels' EPUBs and your
// Kindle notebooks. The same file uploaded twice is kept once. Deleting moves a file to the trash for 30 days first.

const KIND_LABEL = { image: 'image', audio: 'audio', video: 'video', book: 'book', pdf: 'PDF', document: 'document', other: 'file' };

const bytes = (n) => {
  if (!n) return '0 KB';
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(2)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
};
const money = (usd) => (usd < 0.01 ? 'under 1¢' : usd < 1 ? `about ${Math.round(usd * 100)}¢` : `about $${usd.toFixed(2)}`);
const shortDate = (iso) => (iso ? new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '');

const KindIcon = ({ kind }) => {
  const paths = {
    image: <><rect x="4" y="5" width="16" height="14" rx="2" /><circle cx="9" cy="10" r="1.6" /><path d="M20 16l-5-5-8 8" /></>,
    audio: <><path d="M9 18V6l10-2v12" /><circle cx="7" cy="18" r="2" /><circle cx="17" cy="16" r="2" /></>,
    video: <><rect x="3.5" y="6" width="12" height="12" rx="2" /><path d="M15.5 10.5l5-3v9l-5-3" /></>,
    book: <><path d="M12 6.5C10.3 5.2 7.8 4.5 4 4.5v13c3.8 0 6.3.7 8 2 1.7-1.3 4.2-2 8-2v-13c-3.8 0-6.3.7-8 2z" /><path d="M12 6.5v13" /></>,
    pdf: <><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4" /><path d="M9 13h6M9 16.5h4" /></>,
    document: <><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4" /><path d="M9 11h6M9 14h6M9 17h4" /></>,
    other: <><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4" /></>,
  };
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#c9935a" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[kind] || paths.other}
    </svg>
  );
};

export default function Vault() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [data, setData] = useState(null);
  const [trash, setTrash] = useState(false);
  const [folder, setFolder] = useState('');
  const [q, setQ] = useState('');
  const [uploadFolder, setUploadFolder] = useState('general');
  const [newFolder, setNewFolder] = useState('');
  const [uploads, setUploads] = useState([]);        // [{name, state: 'sending'|'done'|'failed', error}]
  const [openId, setOpenId] = useState('');          // the file whose actions are open
  const [renameDraft, setRenameDraft] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    load(false);
  }, []);

  const describeError = (err) => {
    const d = err?.response?.data?.detail;
    return typeof d === 'string' ? d : (err?.message || 'something went wrong');
  };

  const load = (inTrash = trash) => axios.get(`${API}/vault/files?token=${token}&trash=${inTrash ? 'true' : 'false'}`)
    .then(res => setData(res.data))
    .catch(err => setErrorMsg(`couldn't load the vault: ${describeError(err)}`));

  const switchTrash = (v) => { setTrash(v); setOpenId(''); load(v); };

  const onPick = (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = '';
    if (!list.length) return;
    const dest = (newFolder.trim() || uploadFolder || 'general');
    setUploads(list.map(f => ({ name: f.name, state: 'sending' })));
    setErrorMsg('');
    const sendOne = (i) => {
      if (i >= list.length) { setNewFolder(''); load(false); return; }
      const fd = new FormData();
      fd.append('file', list[i]);
      axios.post(`${API}/vault/files?token=${token}&folder=${encodeURIComponent(dest)}`, fd)
        .then(() => setUploads(u => u.map((x, j) => (j === i ? { ...x, state: 'done' } : x))))
        .catch(err => setUploads(u => u.map((x, j) => (j === i ? { ...x, state: 'failed', error: describeError(err) } : x))))
        .finally(() => sendOne(i + 1));
    };
    sendOne(0);
  };

  const openFile = (f, download = false) => {
    // open the window first (Safari blocks one opened after waiting), then send it to the link
    const w = window.open('', '_blank');
    axios.get(`${API}/vault/files/${f.id}/link?token=${token}&download=${download ? 'true' : 'false'}`)
      .then(res => { if (w) w.location = res.data.url; else window.location.href = res.data.url; })
      .catch(err => { if (w) w.close(); setErrorMsg(`couldn't open it: ${describeError(err)}`); });
  };

  const update = (f, body) => axios.put(`${API}/vault/files/${f.id}`, { token, ...body })
    .then(() => load()).catch(err => setErrorMsg(`couldn't change it: ${describeError(err)}`));
  const toTrash = (f) => axios.delete(`${API}/vault/files/${f.id}?token=${token}`)
    .then(() => { setOpenId(''); load(); }).catch(err => setErrorMsg(`couldn't move it to the trash: ${describeError(err)}`));
  const restore = (f) => axios.post(`${API}/vault/files/${f.id}/restore?token=${token}`)
    .then(() => load()).catch(err => setErrorMsg(`couldn't restore it: ${describeError(err)}`));
  const forever = (f) => axios.delete(`${API}/vault/files/${f.id}/forever?token=${token}`)
    .then(() => { setOpenId(''); load(); }).catch(err => setErrorMsg(`couldn't delete it: ${describeError(err)}`));

  const files = data?.files || [];
  const folders = [...new Set([...(data && !trash ? files.map(f => f.folder) : []), 'general'])].sort((a, b) => a.localeCompare(b));
  const needle = q.trim().toLowerCase();
  const shown = files.filter(f => (!folder || f.folder === folder) && (!needle || `${f.name} ${f.note || ''}`.toLowerCase().includes(needle)));
  const connected = data ? data.connected : true;

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap" rel="stylesheet" />
      <header style={s.header}>
        <button style={s.back} onClick={() => (trash ? switchTrash(false) : navigate('/companions'))} aria-label="back">←</button>
        <div style={s.title}>{trash ? 'vault · trash' : 'vault'}</div>
        {!trash && connected && (
          <>
            <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={onPick} />
            <button style={s.addBtn} onClick={() => fileRef.current?.click()}>+ add files</button>
          </>
        )}
      </header>

      <main style={s.main}>
        {errorMsg && <div style={s.error} onClick={() => setErrorMsg('')}>⚠ {errorMsg}</div>}

        {!connected && (
          <section style={s.connectCard}>
            <div style={s.connectTitle}>connect your vault</div>
            <ol style={s.steps}>
              <li>In Railway, open your project and add a <b>Bucket</b> (New → Bucket).</li>
              <li>Open your backend service → <b>Variables</b>, and add the bucket's <code>BUCKET</code>, <code>ACCESS_KEY_ID</code>, <code>SECRET_ACCESS_KEY</code>, <code>ENDPOINT</code> and <code>REGION</code> as variable references.</li>
              <li>Make sure <code>boto3</code> is in your backend's <code>requirements.txt</code>, then let it redeploy.</li>
            </ol>
            <div style={s.hint}>storage costs about 1.5¢ per GB a month, and opening your files is free.</div>
          </section>
        )}

        {connected && data && !trash && (
          <div style={s.usage}>
            <span>{data.file_count} file{data.file_count === 1 ? '' : 's'} · {bytes(data.used_bytes)}</span>
            <span>{money(data.est_monthly_usd)} a month</span>
          </div>
        )}

        {connected && !trash && (
          <div style={s.uploadRow}>
            <span style={s.smallLabel}>new files go in</span>
            <select style={s.select} value={uploadFolder} onChange={e => { setUploadFolder(e.target.value); setNewFolder(''); }} aria-label="folder for new files">
              {folders.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
            <input style={{ ...s.input, width: '150px' }} value={newFolder} onChange={e => setNewFolder(e.target.value)} placeholder="or a new folder" aria-label="new folder" />
          </div>
        )}

        {uploads.length > 0 && (
          <div style={s.uploads}>
            {uploads.map((u, i) => (
              <div key={i} style={s.uploadItem}>
                <span style={s.ellipsis}>{u.name}</span>
                <span style={{ color: u.state === 'failed' ? '#e0a0a0' : u.state === 'done' ? '#c9935a' : '#8a7466' }}>
                  {u.state === 'sending' ? 'sending…' : u.state === 'done' ? 'saved' : u.error || 'failed'}
                </span>
              </div>
            ))}
            {uploads.every(u => u.state !== 'sending') && <button style={s.quiet} onClick={() => setUploads([])}>clear</button>}
          </div>
        )}

        {connected && (
          <>
            {!trash && folders.length > 1 && (
              <div style={s.filters}>
                <button style={s.filterBtn(!folder)} onClick={() => setFolder('')}>all</button>
                {folders.map(f => <button key={f} style={s.filterBtn(folder === f)} onClick={() => setFolder(f)}>{f}</button>)}
              </div>
            )}
            {files.length > 4 && (
              <input style={s.input} value={q} onChange={e => setQ(e.target.value)} placeholder="search your files…" aria-label="search" />
            )}
            {trash && <div style={s.hint}>files here are deleted for good {data?.trash_days || 30} days after you moved them.</div>}

            <div style={s.list}>
              {shown.map(f => (
                <div key={f.id} style={s.item}>
                  <div style={s.itemTop}>
                    <button style={s.itemMain} onClick={() => (trash ? setOpenId(openId === f.id ? '' : f.id) : openFile(f))} title={trash ? '' : 'open'}>
                      <span style={s.icon}><KindIcon kind={f.kind} /></span>
                      <span style={{ minWidth: 0, flex: 1 }}>
                        <span style={{ ...s.name, ...s.ellipsis }}>{f.name}</span>
                        <span style={s.meta}>
                          {KIND_LABEL[f.kind] || 'file'} · {bytes(f.size)} · {shortDate(f.created_at)}{!folder || trash ? ` · ${f.folder}` : ''}
                          {trash && f.deleted_at ? ` · trashed ${shortDate(f.deleted_at)}` : ''}
                        </span>
                      </span>
                    </button>
                    <button style={s.more} onClick={() => { setOpenId(openId === f.id ? '' : f.id); setRenameDraft(f.name); }} aria-label="file actions" aria-expanded={openId === f.id}>⋯</button>
                  </div>
                  {f.source === 'fanfic' && f.ref_id && (
                    <button style={s.fromFic} onClick={() => navigate(`/fanfiction/${f.ref_id}`)}>from fanfiction →</button>
                  )}
                  {f.source === 'novel' && f.ref_id && (
                    <button style={s.fromFic} onClick={() => navigate(`/novels/${f.ref_id}`)}>from novels →</button>
                  )}
                  {openId === f.id && (
                    <div style={s.actions}>
                      {trash ? (
                        <>
                          <button style={s.action} onClick={() => restore(f)}>restore</button>
                          <button style={{ ...s.action, color: '#e0a0a0' }} onClick={() => forever(f)}>delete forever</button>
                        </>
                      ) : (
                        <>
                          <button style={s.action} onClick={() => openFile(f, true)}>download</button>
                          <span style={s.inline}>
                            <input style={{ ...s.input, padding: '5px 8px', width: '170px' }} value={renameDraft} onChange={e => setRenameDraft(e.target.value)} aria-label="name" />
                            {renameDraft.trim() && renameDraft !== f.name && <button style={s.action} onClick={() => update(f, { name: renameDraft })}>rename</button>}
                          </span>
                          <select style={{ ...s.select, padding: '5px 8px' }} value={f.folder} onChange={e => update(f, { folder: e.target.value })} aria-label="move to folder">
                            {folders.map(x => <option key={x} value={x}>{x}</option>)}
                          </select>
                          <button style={{ ...s.action, color: '#e0a0a0' }} onClick={() => toTrash(f)}>move to trash</button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {data && !shown.length && (
                <div style={s.empty}>{trash ? 'the trash is empty.' : files.length ? 'nothing matches.' : 'nothing here yet — tap “+ add files”.'}</div>
              )}
            </div>

            {!trash && (data?.trash_count || 0) > 0 && (
              <button style={s.trashLink} onClick={() => switchTrash(true)}>trash ({data.trash_count}) →</button>
            )}
          </>
        )}
      </main>
    </div>
  );
}

const s = {
  page: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: '#0e0b0f', color: '#e8ddd0', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 },
  header: { position: 'sticky', top: 0, zIndex: 10, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '12px', background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)', borderBottom: '1px solid rgba(201,147,90,0.06)' },
  back: { width: '36px', height: '36px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent', color: '#c9935a', fontSize: '16px', cursor: 'pointer', flexShrink: 0 },
  title: { flex: 1, fontFamily: "'Cormorant Garamond', serif", fontSize: '21px', fontStyle: 'italic' },
  addBtn: { padding: '8px 14px', borderRadius: '18px', border: 'none', background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f', fontSize: '12.5px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: 400, flexShrink: 0 },
  main: { maxWidth: '640px', margin: '0 auto', padding: '18px 20px 90px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '12px' },
  error: { background: 'rgba(180,60,60,0.12)', border: '1px solid rgba(220,100,100,0.3)', borderRadius: '12px', padding: '10px 14px', fontSize: '12px', color: '#e0a0a0', cursor: 'pointer' },
  connectCard: { background: '#1e1825', border: '1px solid rgba(201,147,90,0.14)', borderRadius: '18px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' },
  connectTitle: { fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', fontSize: '20px' },
  steps: { margin: 0, paddingLeft: '20px', fontSize: '13px', lineHeight: 1.7, color: '#cdbfae' },
  hint: { fontSize: '12px', color: '#8a7466', lineHeight: 1.6 },
  usage: { display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '12.5px', color: '#8a7466', padding: '0 2px' },
  uploadRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  smallLabel: { fontSize: '10.5px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6d5d4e' },
  select: { background: '#120f18', border: '1px solid rgba(201,147,90,0.15)', borderRadius: '10px', padding: '8px 10px', color: '#e8ddd0', fontSize: '12.5px', fontFamily: "'DM Sans', sans-serif", outline: 'none', maxWidth: '100%' },
  input: { background: '#120f18', border: '1px solid rgba(201,147,90,0.15)', borderRadius: '10px', padding: '9px 11px', color: '#e8ddd0', fontSize: '13px', fontFamily: "'DM Sans', sans-serif", outline: 'none', boxSizing: 'border-box', maxWidth: '100%' },
  uploads: { background: '#17131b', borderRadius: '14px', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '6px' },
  uploadItem: { display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '12px' },
  ellipsis: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' },
  quiet: { alignSelf: 'flex-end', padding: '2px 4px', border: 'none', background: 'transparent', color: '#8a7466', fontSize: '11.5px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  filters: { display: 'flex', gap: '4px', flexWrap: 'wrap' },
  filterBtn: (on) => ({ padding: '6px 11px', borderRadius: '14px', border: 'none', background: on ? 'rgba(201,147,90,0.14)' : 'transparent', color: on ? '#c9935a' : '#6d5d4e', fontSize: '12.5px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }),
  list: { display: 'flex', flexDirection: 'column', gap: '8px' },
  item: { background: '#17131b', border: '1px solid rgba(201,147,90,0.08)', borderRadius: '16px', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' },
  itemTop: { display: 'flex', alignItems: 'center', gap: '8px' },
  itemMain: { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '12px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', textAlign: 'left', fontFamily: 'inherit', fontWeight: 300 },
  icon: { width: '38px', height: '38px', borderRadius: '11px', background: 'rgba(201,147,90,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  name: { fontSize: '14px', color: '#e8ddd0' },
  meta: { display: 'block', fontSize: '11.5px', color: '#6d5d4e', marginTop: '2px' },
  more: { width: '32px', height: '32px', borderRadius: '50%', border: 'none', background: 'transparent', color: '#8a7466', fontSize: '18px', cursor: 'pointer', flexShrink: 0 },
  fromFic: { alignSelf: 'flex-start', marginLeft: '50px', background: 'none', border: 'none', padding: 0, color: '#c9935a', fontSize: '11.5px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  actions: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', paddingLeft: '50px' },
  action: { background: 'none', border: 'none', padding: 0, color: '#c9935a', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  inline: { display: 'inline-flex', alignItems: 'center', gap: '6px' },
  empty: { textAlign: 'center', color: '#6d5d4e', fontSize: '13px', padding: '30px 12px' },
  trashLink: { alignSelf: 'center', marginTop: '10px', background: 'none', border: 'none', color: '#6d5d4e', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
};
